import { map, flatMap, zip } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DataService, ApiService, InspectionPluginService } from '../../shared/services/index';
import { InspectionPlugin } from '../../shared/models/index';
import { AttachementsPluginFactoryService } from './attachment-plugin-factory.service';

@Injectable()
export class AttachementsPluginService extends InspectionPluginService {
  private editedImagesIds = [];

  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    private attachmentFactory: AttachementsPluginFactoryService
  ) {
    super(apiService, dataService, null, attachmentFactory);
  }

  public addFiles(inspectionId: string, pointId: string, pluginId: string, files: any): Observable<any> {
    return this._addFiles(inspectionId, pointId, pluginId, files).pipe(
      map((plugin: InspectionPlugin) => {
        this.dataService.setChangedPoint(pointId);
        return this.dataService.updatePlugin(inspectionId, plugin);
      })
    );
  }

  public saveAttachments(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assets: any[],
    files: any[]
  ): Observable<any> {
    return this._saveAttachments(inspectionId, pointId, pluginId, assets, files).pipe(
      map((plugin: InspectionPlugin) => {
        this.dataService.setChangedPoint(pointId);
        return this.dataService.updatePlugin(inspectionId, plugin);
      })
    );
  }

  public deleteFile(inspectionId: string, pointId: string, pluginId: string, assetId: string): Observable<any> {
    return this._deleteFile(inspectionId, pointId, pluginId, assetId).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.deleteFile(inspectionId, res);
      })
    );
  }

  public deleteAttachmentPlugin(inspectionId: string, pointId: string, pluginId: string, assets: string[]): any {
    return this.attachmentFactory.deleteFiles(inspectionId, pointId, pluginId, assets).pipe(
      flatMap((res) => {
        return super.deletePlugin(inspectionId, pointId, pluginId);
      }),
      map((success) => success)
    );
  }

  private _addFiles(inspectionId: string, pointId: string, pluginId: string, files: any): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/attachements/${pluginId}`;
    return this.attachmentFactory.addFiles(inspectionId, pointId, pluginId, files).pipe(
      flatMap((data) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(data.plugin);
        }
        data.assetValues.forEach((assetValue) => files.append(assetValue.key, assetValue.assetId));
        return this.apiService.postFormData(url, files).pipe(
          zip(this.apiService.post(data.plugin.postServerUrl, data.plugin.config)),
          map(([res, plugin]) => plugin)
        );
      })
    );
  }

  private _saveAttachments(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    attachments: any[],
    files: any
  ): Observable<any> {
    const url = `/inspections/${inspectionId}/points/${pointId}/attachements/${pluginId}`;
    return this.attachmentFactory.addFiles(inspectionId, pointId, pluginId, files, [...attachments]).pipe(
      flatMap((data) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(data.plugin);
        }
        const inputFile = new FormData();
        for (let i = 0; i < files.length; i++) {
          inputFile.append('files[' + i + ']', files[i], files[i].name);
        }
        data.assetValues.forEach((assetValue) => inputFile.append(assetValue.key, assetValue.assetId));
        return this.apiService.postFormData(url, inputFile).pipe(
          zip(this.apiService.post(data.plugin.postServerUrl, data.plugin.config)),
          map(([res, plugin]) => plugin)
        );
      })
    );
  }

  private _deleteFile(inspectionId: string, pointId: string, pluginId: string, assetId: string): Observable<any> {
    return this.attachmentFactory.deleteFile(inspectionId, pointId, pluginId, assetId).pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(plugin.postServerUrl, plugin.config);
      })
    );
  }
}
